import * as Select from "@radix-ui/react-select";
import classNames from "classnames/bind";
import { isEqual, isPlainObject } from "es-toolkit/compat";
import React, { KeyboardEventHandler, useEffect, useMemo } from "react";
import {
  useController,
  type FieldValues,
  type ValidateResult,
} from "react-hook-form";
import { Button } from "../../Button";
import { mergeProps } from "../../utils/component-props";
import { usePrevious } from "../../utils/hooks/usePrevious";
import { InputWithLabels } from "../_components/InputWithLabels";
import { DropdownProvider, useDropdownContext } from "./DropdownContext";
import DropdownGroupSelector from "./DropdownGroupSelector";
import DropdownIcon from "./DropdownIcon";
import styles from "./DropdownInput.module.scss";
import DropdownMenuContent from "./DropdownMenuContent/DropdownMenuContent";
import { DropdownSearchInput } from "./DropdownValue/DropdownSearchInput";
import DropdownValue from "./DropdownValue/DropdownValue";
import type { ControlledDropdownInputProps, DropdownProps } from "./types";

const cx = classNames.bind(styles);

// IMPROVE: this component as a whole and all of its sub-components needs a
// comprehensive refactor and needs to be renamed "Combobox". A partial refactor
// was completed in March 2024. Another partial refactor was completed in July 2025.

const ComboboxInput = () => {
  const {
    value,
    onChange,
    isMulti = false,
    isFluid = false,
    isInline = false,
    isBusy = false,
    "aria-invalid": ariaInvalid,
    error,
    invalidMessage,
    isLoading = false,
    isDisabled = false,
    isClearable = true,
    isSearchable = true,
    id,
    labelledById,
    triggerRef,
    clearTriggerRef,
    contentRef,
    searchInputRef,
    isMenuOpen,
    openMenu,
    closeMenu,
    clear,
    hasGroups,
    checkForUnknownSelectedValue,
    flairSlot,
    leftSlot,
    style,
    className,
  } = useDropdownContext();
  const prevValue = usePrevious(value);

  const handleKeyDown: KeyboardEventHandler<HTMLElement> = (event) => {
    const { code, target, shiftKey } = event;
    const options: HTMLElement[] = [
      searchInputRef.current as HTMLElement,
      clearTriggerRef.current as HTMLElement,
    ].concat(
      Array.from(
        // @ts-expect-error target is supposed to inherit HTMLDivElement props
        contentRef.current.querySelectorAll("[role='option']")
      )
    );

    const currentFocusIdx = options.indexOf(target as HTMLElement);

    switch (code) {
      case "Escape":
        closeMenu?.();

        break;
      case "ArrowUp":
        if (currentFocusIdx <= 0) {
          break;
        }

        options[currentFocusIdx - 1].focus();
        break;
      case "Tab":
        if (shiftKey) {
          if (currentFocusIdx <= 0) {
            event.preventDefault();
            break;
          }

          options[currentFocusIdx - 1]?.focus();
        } else {
          if (currentFocusIdx === options.length - 1) {
            event.preventDefault();
            break;
          }

          options[currentFocusIdx + 1]?.focus();
        }

        break;
      case "ArrowDown":
        if (currentFocusIdx > options.length - 1) {
          break;
        }

        options[currentFocusIdx + 1]?.focus();
        break;
    }
  };

  useEffect(() => {
    if (isEqual(prevValue, value)) return;

    const group = hasGroups ? value?.[0] : null;
    const valueToEvaluate = hasGroups ? value?.[1] : value;

    if (isMulti) {
      if (valueToEvaluate && !Array.isArray(valueToEvaluate)) {
        onChange?.(hasGroups ? [group, [valueToEvaluate]] : [valueToEvaluate]);
      } else {
        onChange?.(
          hasGroups ? [group, valueToEvaluate ?? []] : (valueToEvaluate ?? [])
        );
      }
    } else {
      if (valueToEvaluate && Array.isArray(valueToEvaluate)) {
        onChange?.(
          hasGroups ? [group, valueToEvaluate?.[0]] : valueToEvaluate?.[0]
        );
      }
    }
  }, [value, isMulti, hasGroups]);

  return (
    <Select.Root
      open={isMenuOpen}
      onOpenChange={(open) => {
        if (open && !isLoading && !isDisabled) {
          openMenu();
        } else {
          checkForUnknownSelectedValue();
          closeMenu();
        }
      }}
    >
      <div className={cx("select-container", { isFluid, isInline })}>
        {leftSlot?.({ className: cx("left-slot") })}
        <Select.Trigger asChild>
          <div
            role="combobox"
            className={cx("select", className, {
              skeleton: isLoading,
              isBusy,
              isMulti,
              isFluid,
              isDisabled,
              hasError: error || ariaInvalid === true || !!invalidMessage,
              hasLeftSlot: !!leftSlot,
            })}
            ref={triggerRef}
            tabIndex={0}
            aria-labelledby={labelledById}
            aria-disabled={isDisabled}
            aria-invalid={ariaInvalid}
            id={id}
            style={style}
          >
            <DropdownValue />
            {flairSlot?.({ className: cx("flair-slot") })}
            <DropdownIcon />
          </div>
        </Select.Trigger>
      </div>
      <Select.Portal>
        <Select.Content
          avoidCollisions
          position="popper"
          className={cx("popover-content")}
          onKeyDown={handleKeyDown}
        >
          <div ref={contentRef}>
            {hasGroups && (
              <div className={cx("group-switcher-container")}>
                <DropdownGroupSelector />
              </div>
            )}
            {isSearchable && (
              <div className={cx("input-container")}>
                <DropdownSearchInput />
                {isClearable && (
                  <Button
                    variant="outline"
                    onClick={clear}
                    className={cx("clear-trigger")}
                    ref={clearTriggerRef}
                  >
                    Clear
                  </Button>
                )}
              </div>
            )}
            <DropdownMenuContent />
          </div>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );
};

/**
 * The dropdown allows users to select one or more data values from a list of options and load them into the input field.
 */
const DropdownInput = (props: DropdownProps) => (
  <DropdownProvider {...props}>
    <ComboboxInput />
  </DropdownProvider>
);

DropdownInput.displayName = "DropdownInput";

export const ControlledDropdownInput = <T extends FieldValues>({
  name,
  defaultValue,
  rules,
  label,
  isLabelShown,
  labelPosition,
  description,
  isRequired,
  isOptionalTextShown,
  shouldShowDescriptionInTooltip,
  isInline = false,
  isFluid = true,
  ...restProps
}: ControlledDropdownInputProps<T>) => {
  const { isDisabled, isLoading, isBusy } = restProps;
  const { field, fieldState } = useController({
    name,
    // @ts-expect-error expects a certain shape but it's fine
    defaultValue,
    rules: {
      ...rules,
      validate:
        rules?.validate && typeof rules.validate === "function"
          ? rules.validate
          : {
              ...(isRequired
                ? {
                    isRequired: (value: string): ValidateResult =>
                      [null, undefined, ""].includes(value) ||
                      (Array.isArray(value) && !value.length)
                        ? `${typeof label === "string" ? label : "Combobox input"} is required`
                        : undefined,
                  }
                : {}),
              ...(rules?.validate && isPlainObject(rules.validate)
                ? rules.validate
                : {}),
            },
    },
  });

  const mergedProps = useMemo(() => {
    return mergeProps(restProps, {
      onChange: field.onChange,
      onBlur: field.onBlur,
      disabled: field.disabled,
      name: field.name,
      ref: field.ref,
    });
  }, [
    restProps,
    field.onChange,
    field.onBlur,
    field.disabled,
    field.name,
    field.ref,
  ]);

  const invalidMessage = fieldState.invalid
    ? fieldState.error?.message ||
      `Input is invalid (${fieldState.error?.type})`
    : undefined;

  return (
    <InputWithLabels
      colorScheme="light"
      isDisabled={isDisabled ?? false}
      isLoading={isLoading ?? false}
      isBusy={isBusy ?? false}
      label={label}
      isLabelShown={isLabelShown}
      labelPosition={labelPosition}
      description={description}
      isRequired={isRequired}
      isOptionalTextShown={isOptionalTextShown}
      shouldShowDescriptionInTooltip={shouldShowDescriptionInTooltip}
      isInline={isInline}
      isFluid={isFluid}
      invalidMessage={invalidMessage}
    >
      {({ invalidMessageId, labelId, inputId, flairSlot, leftSlot }) => (
        <DropdownInput
          id={inputId}
          labelledById={labelId}
          error={fieldState.error}
          invalidMessage={invalidMessage}
          aria-invalid={fieldState.invalid}
          aria-describedby={invalidMessageId}
          {...(!isLabelShown && !description && typeof label === "string"
            ? { "aria-label": label }
            : {})}
          flairSlot={flairSlot}
          leftSlot={leftSlot}
          isInline={isInline}
          isFluid={isFluid}
          {...mergedProps}
          value={field.value}
        />
      )}
    </InputWithLabels>
  );
};

ControlledDropdownInput.displayName = "ControlledDropdownInput";

export default DropdownInput;
